import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { useTranslation } from 'next-i18next';
import { useViewport } from '@/hooks/useViewport';
import { XIcon } from '@/icons';
import { cn } from '@/utils/tailwind.util';
import { IconButton } from '../button';
import { Drawer, DrawerContent } from '../drawer';
type DialogSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
const WidthClassnames: Record<DialogSize, string> = {
  xs: 'w-[295px] mobile-lg:w-[320px]',
  sm: 'w-[400px]',
  md: 'w-[420px] mobile-lg:w-[520px]',
  lg: 'w-full mobile-lg:w-[720px]',
  xl: 'w-full mobile-lg:w-[960px]',
  '2xl': 'w-full mobile-lg:w-[1200px]'
};
export interface ResponsiveDialogProps {
  onClose?: (param?: unknown) => void;
  size?: DialogSize;
  className?: string;
  hasDrawerHandle?: boolean;
  hasDim?: boolean;
  shouldCloseOnDimClick?: boolean;
  /** Force drawer mode on mobile regardless of size. */
  forceDrawerOnMobile?: boolean;
  /** Use a caller-provided mobile breakpoint when its Figma layout differs by one pixel. */
  isMobileLayout?: boolean;
  /**
   * 열릴 때 딤·창 등장 애니메이션을 끈다. 한 창을 닫고 바로 다음 창을 여는 전환
   * (공유 → 클래스 연결 등)에서 딤이 사라졌다 다시 어두워지며 깜빡이는 것을 막는다.
   */
  disableOpenAnimation?: boolean;
  /** 모바일 시트(drawer)일 때 핸들을 아래로 끌어 닫을 수 있게 한다. 닫히면 onClose 를 부른다. */
  isSwipeDismissible?: boolean;
  /** 가운데 창에서 ESC 를 눌렀을 때 부른다. 없으면 ESC 로 닫히지 않는다(닫을 수 없는 창이 있어 기본값이 아니다). */
  onEscapeKeyDown?: () => void;
  CustomDim?: React.ForwardRefExoticComponent<React.RefAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement>>;
}
const ResponsiveDialog = ({
  children,
  onClose,
  size = 'xs',
  className,
  hasDrawerHandle = false,
  hasDim = true,
  shouldCloseOnDimClick = true,
  forceDrawerOnMobile = false,
  isMobileLayout,
  disableOpenAnimation = false,
  isSwipeDismissible = false,
  onEscapeKeyDown,
  CustomDim,
  ...others
}: React.PropsWithChildren<ResponsiveDialogProps & DialogPrimitive.DialogProps>) => {
  const {
    isMobile
  } = useViewport();
  const isDrawer = (isMobileLayout ?? isMobile) && (forceDrawerOnMobile || ['lg', 'xl', '2xl'].includes(size));
  return isDrawer ? <Drawer {...others} open={true} dismissible={isSwipeDismissible} onOpenChange={isSwipeDismissible ? open => {
    if (!open) onClose?.(false);
  } : undefined} data-sentry-element="Drawer" data-sentry-component="ResponsiveDialog" data-sentry-source-file="dialog.tsx">
      <DrawerContent className={cn('!max-h-[calc(90%-env(safe-area-inset-top))] overflow-hidden', WidthClassnames[size],
    // 하단 시트는 크기와 무관하게 모바일에서 전폭이어야 한다.
    // 자동 drawer 대상(lg/xl/2xl)은 이미 w-full 이지만 xs/sm/md 는 고정폭이라
    // forceDrawerOnMobile 로 시트가 되면 좌우에 여백이 남아 다른 모달과 달라 보인다.
    'max-mobile-lg:w-full', className)} hasDrawerHandle={hasDrawerHandle} hasDim={hasDim} onDimClick={shouldCloseOnDimClick ? () => onClose?.(false) : undefined} CustomDim={CustomDim} data-sentry-element="DrawerContent" data-sentry-source-file="dialog.tsx">
        {children}
      </DrawerContent>
    </Drawer> : <Dialog {...others} open={true} data-sentry-element="Dialog" data-sentry-component="ResponsiveDialog" data-sentry-source-file="dialog.tsx">
      <DialogContent className={cn(WidthClassnames[size], 'overflow-hidden max-h-[calc(90%-env(safe-area-inset-top))] max-w-[calc(100vw-32px)] mobile-lg:max-w-[calc(100vw-48px)] tablet-lg:max-w-[calc(100vw-80px)] flex flex-col', className)} hasDim={hasDim} disableOpenAnimation={disableOpenAnimation} onDimClick={shouldCloseOnDimClick ? () => onClose?.(false) : undefined} onEscapeKeyDown={onEscapeKeyDown} CustomDim={CustomDim} data-sentry-element="DialogContent" data-sentry-source-file="dialog.tsx">
        {children}
      </DialogContent>
    </Dialog>;
};
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;
const DialogTitle = DialogPrimitive.Title;
const DialogDescription = DialogPrimitive.Description;
const DialogOverlay = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Overlay>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
  disableOpenAnimation?: boolean;
}>(({
  className,
  disableOpenAnimation,
  ...props
}, ref) => <DialogPrimitive.Overlay ref={ref} className={cn('fixed inset-0 z-modal bg-[rgba(28,27,31,0.24)] transition-all duration-200', !disableOpenAnimation && 'data-[state=open]:animate-in data-[state=open]:fade-in-0', 'data-[state=closed]:animate-out data-[state=closed]:fade-out-0', className)} {...props} />);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  hasDim: boolean;
  disableOpenAnimation?: boolean;
  CustomDim?: React.ForwardRefExoticComponent<React.RefAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement>>;
  onDimClick?: () => void;
}>(({
  className,
  children,
  hasDim,
  disableOpenAnimation,
  CustomDim,
  onDimClick,
  ...props
}, ref) => {
  return <DialogPortal>
        {hasDim && (CustomDim ? <CustomDim onClick={onDimClick} /> : <DialogOverlay onClick={onDimClick} disableOpenAnimation={disableOpenAnimation} />)}
        <DialogPrimitive.Content ref={ref} aria-describedby={undefined} className={cn('rounded-lg overflow-hidden fixed left-[50%] top-[50%] z-modal grid w-full translate-x-[-50%] translate-y-[-50%] shadow-xl bg-white p-0 duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]', !disableOpenAnimation && 'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]', hasDim && 'shadow-2xl', className)} {...props}>
          {children}
        </DialogPrimitive.Content>
      </DialogPortal>;
});
DialogContent.displayName = DialogPrimitive.Content.displayName;
const _DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => <div className={cn('text-heading-md-bold pt-md px-md pb-xs', 'mobile-lg:pt-lg mobile-lg:pb-xs mobile-lg:px-lg', className)} {...props} data-sentry-component="_DialogHeader" data-sentry-source-file="dialog.tsx" />;
_DialogHeader.displayName = '_DialogHeader';
export interface DialogHeaderProps {
  title?: string;
  ariaTitle?: string;
  className?: string;
  hasCloseButton?: boolean;
  topSlot?: React.ReactNode;
  leftSlot?: React.ReactNode;
  rightSlot?: React.ReactNode;
  onClose?: (param?: unknown) => void;
}
const DialogCloseButton = ({
  onClose
}: Pick<DialogHeaderProps, 'onClose'>) => {
  const {
    t
  } = useTranslation(['common']);
  return <IconButton className="ml-auto hover:bg-transparent" size="sm" variant="transparent" intent="neutral" aria-label={t('common:global.close.button')} onClick={() => onClose?.(false)} data-sentry-element="IconButton" data-sentry-component="DialogCloseButton" data-sentry-source-file="dialog.tsx">
      <XIcon size="md" data-sentry-element="XIcon" data-sentry-source-file="dialog.tsx" />
    </IconButton>;
};
const DialogHeader = (props: DialogHeaderProps) => <_DialogHeader className={cn('flex flex-col', props.className)} data-sentry-element="_DialogHeader" data-sentry-component="DialogHeader" data-sentry-source-file="dialog.tsx">
    {props.topSlot}
    <div className="flex items-center">
      {props.leftSlot}
      {props.title ? <DialogTitle>{props.title}</DialogTitle> : <VisuallyHidden.Root>
          <DialogTitle>{props.ariaTitle ?? 'Dialog Title'}</DialogTitle>
        </VisuallyHidden.Root>}
      <div className="ml-auto flex items-center">
        {props.rightSlot}
        {props.hasCloseButton && <DialogCloseButton onClose={props.onClose} />}
      </div>
    </div>
  </_DialogHeader>;
const DialogBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({
  className,
  ...props
}, ref) => <div className={cn('text-body-md-regular overflow-y-auto h-full', className)} ref={ref} {...props} />);
DialogBody.displayName = 'DialogBody';
const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => <div className={cn('p-md mobile-lg:p-lg', className)} {...props} data-sentry-component="DialogFooter" data-sentry-source-file="dialog.tsx" />;
DialogFooter.displayName = 'DialogFooter';
export { Dialog, DialogPortal, DialogOverlay, DialogClose, DialogTrigger, DialogTitle, DialogDescription, DialogContent, ResponsiveDialog, DialogHeader, DialogBody, DialogFooter };